import { useState, useEffect } from 'react';
import { X, Plus, GripVertical, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Clock } from 'lucide-react';

const COLORS = [
  { id: 'zinc', name: 'Gray', class: 'bg-zinc-500' },
  { id: 'cyan', name: 'Cyan', class: 'bg-cyan-500' },
  { id: 'amber', name: 'Amber', class: 'bg-amber-500' },
  { id: 'emerald', name: 'Green', class: 'bg-emerald-500' },
  { id: 'red', name: 'Red', class: 'bg-red-500' },
  { id: 'violet', name: 'Purple', class: 'bg-violet-500' },
  { id: 'blue', name: 'Blue', class: 'bg-blue-500' },
  { id: 'pink', name: 'Pink', class: 'bg-pink-500' },
];

export default function ColumnSettingsDialog({ project, onClose, onSave }) {
  const [statuses, setStatuses] = useState(
    project?.statuses?.map((s, i) => ({ ...s, order: i })) || []
  );

  // Handle Esc key to close dialog
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(statuses);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);
    setStatuses(items.map((item, index) => ({ ...item, order: index })));
  };

  const addStatus = () => {
    const newId = `status_${Date.now()}`;
    setStatuses([...statuses, { id: newId, title: 'New Status', color: 'zinc', order: statuses.length, is_in_progress: false }]);
  };

  const updateStatus = (index, field, value) => {
    const updated = [...statuses];
    updated[index] = { ...updated[index], [field]: value };
    setStatuses(updated);
  };

  const removeStatus = (index) => {
    if (statuses.length <= 1) return;
    setStatuses(statuses.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i })));
  };

  const handleSave = () => {
    onSave(statuses);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="column-settings-dialog">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-lg max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-zinc-800">
          <h2 className="text-lg font-semibold text-white">Customize Columns</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-6 max-h-[50vh] overflow-y-auto">
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="columns">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-3">
                  {statuses.map((status, index) => (
                    <Draggable key={status.id} draggableId={status.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg ${snapshot.isDragging ? 'shadow-xl' : ''
                            }`}
                        >
                          <div {...provided.dragHandleProps} className="text-zinc-500 cursor-grab">
                            <GripVertical className="w-4 h-4" />
                          </div>
                          <div className={`w-3 h-3 rounded-full ${COLORS.find(c => c.id === status.color)?.class || 'bg-zinc-500'}`} />
                          <Input
                            value={status.title}
                            onChange={(e) => updateStatus(index, 'title', e.target.value)}
                            className="flex-1 bg-zinc-950 border-zinc-700 h-9"
                            data-testid={`column-title-${status.id}`}
                          />
                          <Select value={status.color} onValueChange={(v) => updateStatus(index, 'color', v)}>
                            <SelectTrigger className="w-24 bg-zinc-950 border-zinc-700 h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              {COLORS.map((color) => (
                                <SelectItem key={color.id} value={color.id}>
                                  <div className="flex items-center gap-2">
                                    <div className={`w-3 h-3 rounded-full ${color.class}`} />
                                    {color.name}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <div className="flex items-center gap-2 px-2 border-l border-zinc-700">
                            <Switch
                              id={`in-progress-${status.id}`}
                              checked={status.is_in_progress}
                              onCheckedChange={(v) => updateStatus(index, 'is_in_progress', v)}
                            />
                            <Label htmlFor={`in-progress-${status.id}`} className="text-[10px] uppercase text-zinc-500 font-bold whitespace-nowrap">
                              In Progress
                            </Label>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeStatus(index)}
                            disabled={statuses.length <= 1}
                            className="text-zinc-500 hover:text-red-400 h-9 w-9"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <Button
            variant="outline"
            onClick={addStatus}
            className="w-full mt-4 border-dashed border-zinc-700 text-zinc-400"
            data-testid="add-column-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Column
          </Button>
        </div>

        <div className="flex justify-end gap-2 p-6 border-t border-zinc-800">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} className="bg-cyan-500 text-black hover:bg-cyan-400" data-testid="save-columns-btn">
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}
